/**
 * WebSocket Service for Real-time Collaboration Updates
 */

import logger from '../utils/logger';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 2000;
    this.listeners = new Map();
    this.isConnected = false;
    this.pingInterval = null;
  }

  // Get WebSocket URL from environment
  getWsUrl() {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert http(s) to ws(s)
    const wsUrl = backendUrl.replace(/^http/, 'ws');
    return `${wsUrl}/api/ws/collaboration`;
  }

  // Connect to WebSocket server
  connect() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      if(process.env.NODE_ENV==='development')logger.log('[WS] Already connected');
      return;
    }

    try {
      const wsUrl = this.getWsUrl();
      if(process.env.NODE_ENV==='development')logger.log('[WS] Connecting to:', wsUrl);
      
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        if(process.env.NODE_ENV==='development')logger.log('[WS] Connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.emit('connected', { timestamp: new Date().toISOString() });
        
        // Start ping interval to keep connection alive
        this.startPing();
      };
      
      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          logger.error('[WS] Failed to parse message:', error);
        }
      };
      
      this.ws.onclose = (event) => {
        if(process.env.NODE_ENV==='development')logger.log('[WS] Connection closed:', event.code, event.reason);
        this.isConnected = false;
        this.stopPing();
        this.emit('disconnected', { code: event.code, reason: event.reason });
        
        // Attempt to reconnect
        this.attemptReconnect();
      };
      
      this.ws.onerror = (error) => {
        logger.error('[WS] Error:', error);
        this.emit('error', { error });
      };
      
    } catch (error) {
      logger.error('[WS] Failed to connect:', error);
      this.attemptReconnect();
    }
  }

  // Disconnect from WebSocket server
  disconnect() {
    this.stopPing();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
  }

  // Attempt to reconnect
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      if(process.env.NODE_ENV==='development')logger.log('[WS] Max reconnection attempts reached');
      this.emit('reconnect_failed', {});
      return;
    }
    
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    
    if(process.env.NODE_ENV==="development")logger.log(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      this.connect();
    }, delay);
  }

  // Start ping interval
  startPing() {
    this.pingInterval = setInterval(() => {
      if (this.isConnected) {
        this.send({ type: 'ping' });
      }
    }, 30000); // Ping every 30 seconds
  }

  // Stop ping interval
  stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  // Send a message
  send(data) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      logger.warn('[WS] Cannot send - not connected');
    }
  }

  // Subscribe to a channel
  subscribe(channel) {
    this.send({ type: 'subscribe', channel });
  }

  // Unsubscribe from a channel
  unsubscribe(channel) {
    this.send({ type: 'unsubscribe', channel });
  }

  // Send typing indicator
  sendTyping(channel, user) {
    this.send({ type: 'typing', channel, user });
  }

  // Send stop typing indicator
  sendStopTyping(channel, user) {
    this.send({ type: 'stop_typing', channel, user });
  }

  // Handle incoming messages
  handleMessage(data) {
    const { type, ...rest } = data;
    
    switch (type) {
      case 'connected':
        if(process.env.NODE_ENV==='development')logger.log('[WS] Server confirmed connection');
        break;
        
      case 'subscribed':
        if(process.env.NODE_ENV==='development')logger.log('[WS] Subscribed to channel:', rest.channel);
        this.emit('subscribed', rest);
        break;
        
      case 'unsubscribed':
        if(process.env.NODE_ENV==='development')logger.log('[WS] Unsubscribed from channel:', rest.channel);
        this.emit('unsubscribed', rest);
        break;
        
      case 'pong':
        // Heartbeat response - connection is alive
        break;
        
      case 'new_message':
        this.emit('new_message', rest);
        break;
        
      case 'new_task':
        this.emit('new_task', rest);
        break;
        
      case 'task_updated':
        this.emit('task_updated', rest);
        break;
        
      case 'typing':
        this.emit('typing', rest);
        break;
        
      case 'stop_typing':
        this.emit('stop_typing', rest);
        break;
        
      case 'new_file':
        this.emit('new_file', rest);
        break;
        
      case 'activity':
        this.emit('activity', rest);
        break;
        
      case 'error':
        logger.error('[WS] Server error:', rest.message);
        this.emit('error', rest);
        break;
        
      default:
        if(process.env.NODE_ENV==='development')logger.log('[WS] Unknown message type:', type, rest);
    }
  }

  // Event listener management
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  off(event, callback) {
    this.listeners.get(event)?.delete(callback);
  }

  emit(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          logger.error('[WS] Listener error:', error);
        }
      });
    }
  }

  // Get connection status
  getStatus() {
    return {
      isConnected: this.isConnected,
      readyState: this.ws?.readyState,
      reconnectAttempts: this.reconnectAttempts,
    };
  }
}

// Create singleton instance
const wsService = new WebSocketService();

export default wsService;
